/** API project `status` values (create, update, and list filters). */
import PropTypes from "prop-types";
import { USER_PORTAL_URL } from "config/envConfig";
import { AppShareButton } from "components/common/AppShareButton";
import { noWrap } from "utils/formatters";

export const PROJECT_STATUS_OPTIONS = [
  // { value: "draft", label: "Draft" },
  // { value: "planning", label: "Planning" },
  { value: "active", label: "Active" },
  { value: "on_hold", label: "On Hold" },
  { value: "completed", label: "Completed" },
];

const LEGACY_PROJECT_STATUS_MAP = {
  closed: "completed",
  archived: "draft",
  "on hold": "on_hold",
};

/** Maps API / legacy status strings to a valid `PROJECT_STATUS_OPTIONS` value. */
export function normalizeProjectStatus(status, fallback = "draft") {
  const raw = String(status ?? "").trim().toLowerCase();
  if (!raw || raw === "—") return fallback;
  const key = raw.replaceAll(/\s+/g, "_");
  if (LEGACY_PROJECT_STATUS_MAP[key]) return LEGACY_PROJECT_STATUS_MAP[key];
  if (PROJECT_STATUS_OPTIONS.some((option) => option.value === key)) return key;
  return fallback;
}

export function projectStatusLabel(status) {
  const key = normalizeProjectStatus(status, "");
  if (!key) return "—";
  const match = PROJECT_STATUS_OPTIONS.find((option) => option.value === key);
  return match?.label ?? String(status);
}

export function normalizeIsSample(value) {
  return value === true || value === "true" || value === 1;
}

export function buildUserPortalCampaignUrl(projectId) {
  const id = String(projectId ?? "").trim();
  if (!id) return "";
  return `${USER_PORTAL_URL}/campaign/${encodeURIComponent(id)}`;
}

export function ProjectShareCell(row) {
  return (
    <AppShareButton
      url={row?.shareUrl || buildUserPortalCampaignUrl(row?.id)}
      title={row?.title ? `Share ${row.title}` : "Share campaign"}
      ariaLabel="Share campaign"
      className="!rounded-full"
    />
  );
}

function ProjectIsSampleSwitch({ checked, disabled, onChange, ariaLabel }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        onChange(!checked);
      }}
      className={[
        "relative inline-flex h-6 w-11 shrink-0 rounded-full border transition-colors",
        disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
        checked ? "border-brand-700 bg-brand-900" : "border-slate-300 bg-slate-200",
      ].join(" ")}
    >
      <span
        aria-hidden
        className={[
          "pointer-events-none absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform",
          checked ? "translate-x-5" : "translate-x-0",
        ].join(" ")}
      />
    </button>
  );
}

ProjectIsSampleSwitch.propTypes = {
  checked: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string,
};

export function getProjectIsSampleColumn({ onToggle, updatingId }) {
  function IsSampleCell(row) {
    const checked = Boolean(row.isSample);
    const disabled = updatingId != null && String(updatingId) === String(row.id);

    return (
      <ProjectIsSampleSwitch
        checked={checked}
        disabled={disabled}
        ariaLabel={`Mark ${row.title ?? "project"} as sample project`}
        onChange={(next) => onToggle(row, next)}
      />
    );
  }

  return {
    id: "isSample",
    header: noWrap("Sample"),
    selector: (row) => (row.isSample ? 1 : 0),
    cell: IsSampleCell,
    sortable: true,
    width: "96px",
  };
}

export function getProjectShareColumn() {
  return {
    id: "share",
    header: noWrap("Share"),
    selector: (row) => row.shareUrl,
    cell: ProjectShareCell,
    sortable: false,
    width: "110px",
  };
}

function extractListItems(root) {
  const items =
    root.items ??
    root.projects ??
    root.results ??
    root.documents ??
    root.records ??
    root.rows ??
    [];
  return Array.isArray(items) ? items : [];
}

function extractListTotal(root, items) {
  const meta = root.meta ?? root.pagination ?? {};
  const total =
    root.total ??
    root.totalCount ??
    root.count ??
    meta.total ??
    meta.totalCount ??
    (typeof root.totalPages === "number" && typeof root.limit === "number"
      ? root.totalPages * root.limit
      : items.length);

  const n = Number(total);
  return Number.isFinite(n) ? n : items.length;
}

/**
 * Paginated list parsing for `/api/admin/projects` (same shapes as other admin lists).
 */
export function parseProjectsListResponse(json) {
  let root = json;
  if (json && typeof json === "object" && Object.hasOwn(json, "data")) {
    root = json.data;
  }

  if (Array.isArray(root)) {
    return { items: root, total: root.length };
  }

  if (root && typeof root === "object") {
    const items = extractListItems(root);
    return { items, total: extractListTotal(root, items) };
  }

  return { items: [], total: 0 };
}

function displayTextOrDash(value) {
  if (value == null || value === "") return "—";
  return String(value);
}

function formatMoneyAmount(n, currency = "USD") {
  if (n === null || n === undefined || n === "" || Number.isNaN(Number(n))) {
    return "—";
  }
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 0,
    }).format(Number(n));
  } catch {
    return String(n);
  }
}

function plainDescription(descriptionRaw) {
  return (
    descriptionRaw
      .replaceAll(/<[^>]*>/g, " ")
      .replaceAll(/\s+/g, " ")
      .trim() || "—"
  );
}

export function mapProjectListRow(p) {
  const id = p.id ?? p._id ?? "";
  const currency = p.currency || "USD";
  const fundsNum = p.fundsRaisedAmount ?? p.amountRaised ?? p.raisedAmount;
  const capNum = p.hardCapAmount ?? p.targetAmount ?? p.goalAmount;
  const sharePriceNum = p.sharePrice ?? p.pricePerShare;
  const totalFundsRequiredNum = p.totalFundsRequired ?? p.total_funds_required;
  const descriptionRaw = p.description != null && p.description !== "" ? String(p.description) : "";
  const campaignDaysLeftRaw = p.campaignDaysLeft ?? p.daysLeft ?? p.days_left;
  const campaignDaysLeftNum = Number(campaignDaysLeftRaw);
  const investorCountRaw = p.investorCount ?? p.investor_count ?? p.investorsCount;
  const investorCountNum = Number(investorCountRaw);

  return {
    id: String(id),
    shareUrl: buildUserPortalCampaignUrl(id),
    title: displayTextOrDash(p.title),
    description: descriptionRaw || "—",
    descriptionPlain: plainDescription(descriptionRaw),
    fundsRaised: formatMoneyAmount(p.fundsRaised ?? fundsNum, currency),
    fundsRaisedAmount: Number(p.fundsRaised ?? fundsNum) || 0,
    hardCap: formatMoneyAmount(p.hardCap ?? capNum, currency),
    hardCapAmount: Number(p.hardCap ?? capNum) || 0,
    sharePrice: formatMoneyAmount(sharePriceNum, currency),
    sharePriceAmount: Number(sharePriceNum) || 0,
    totalFundsRequired: formatMoneyAmount(totalFundsRequiredNum, currency),
    totalFundsRequiredAmount: Number(totalFundsRequiredNum) || 0,
    campaignDaysLeft: campaignDaysLeftRaw ?? "—",
    campaignDaysLeftAmount: Number.isFinite(campaignDaysLeftNum) ? campaignDaysLeftNum : -1,
    daysLeft: p.daysLeft ?? p.days_left ?? "—",
    investorCount: investorCountRaw ?? "—",
    investorCountAmount: Number.isFinite(investorCountNum) ? investorCountNum : -1,
    status: displayTextOrDash(p.status),
    isSample: normalizeIsSample(p.isSample ?? p.is_sample),
  };
}

function unwrapProjectPayload(payload) {
  if (payload == null || typeof payload !== "object" || payload.project === undefined) {
    return payload ?? {};
  }
  return payload.project;
}

function collectPrimaryImages(p) {
  if (Array.isArray(p.imageUrl) && p.imageUrl.length) {
    return p.imageUrl;
  }
  if (Array.isArray(p.images) && p.images.length) {
    return p.images;
  }
  if (p.image) {
    return [p.image];
  }
  return [];
}

function collectProjectImages(p) {
  const primaryImages = collectPrimaryImages(p);
  const galleryImages = Array.isArray(p.galleryImages) ? p.galleryImages : [];
  return [...primaryImages, ...galleryImages];
}

function formatOfferingDocumentDetails(p) {
  if (Array.isArray(p.offeringDocuments) && p.offeringDocuments.length) {
    return p.offeringDocuments.map((doc) => `${doc.name}: ${doc.url}`).join("\n");
  }
  return p.offeringDocumentDetails ?? "";
}

function mapDetailTeamMembers(teamMembers) {
  if (!Array.isArray(teamMembers)) return [];
  return teamMembers.map((m) => ({
    name: String(m.name ?? "").trim(),
    position: String(m.position ?? m.positionAtIssuer ?? "").trim(),
    municipality: String(m.municipalityOfResidence ?? m.municipality ?? "").trim(),
    email: String(m.email ?? "").trim(),
    occupation: String(m.principalOccupation ?? m.occupation ?? "").trim(),
    about: String(m.about ?? m.description ?? "").trim(),
    imageUrl: String(m.imageUrl ?? "").trim(),
  }));
}

function mapDetailNewsUpdates(p) {
  const raw = p.newsUpdates ?? p.news_updates ?? p.newsAndUpdates ?? [];
  if (!Array.isArray(raw) || !raw.length) return [];
  return raw.map((item, i) => ({
    id: String(item.id ?? `news-${i}`),
    updateTitle: String(item.updateTitle ?? item.title ?? "").trim(),
    date: item.date ?? item.publishedAt ?? item.published_at ?? "",
    updateDescription: String(
      item.updateDescription ?? item.description ?? item.body ?? "",
    ).trim(),
  }));
}

function resolveDetailFinancials(p) {
  const currency = p.currency || "USD";
  const fundsRaisedNum = p.sidebar?.fundsRaised ?? p.fundsRaised ?? p.fundsRaisedAmount ?? p.amountRaised ?? 0;
  const hardCapNum = p.sidebar?.hardCap ?? p.hardCap ?? p.hardCapAmount ?? p.targetAmount ?? 0;
  const sharePriceNum = p.sidebar?.sharePrice ?? p.sharePrice ?? p.pricePerShare ?? 0;
  const minInvestNum =
    p.sidebar?.minimumInvestmentAmount ?? p.minimumInvestmentAmount ?? p.minimumInvestment ?? 0;
  const maxInvestNum =
    p.sidebar?.maximumInvestmentAmount ?? p.maximumInvestmentAmount ?? p.maximumInvestment ?? 0;

  return {
    currency,
    fundsRaisedNum,
    hardCapNum,
    sharePriceNum,
    minInvestNum,
    maxInvestNum,
  };
}

/** Normalize API project payload for details / context (tabs, carousel, snapshot). */
export function normalizeProjectDetail(payload, fallbackId) {
  const p = unwrapProjectPayload(payload);
  const id = String(p.id ?? p._id ?? fallbackId ?? "");
  const allImages = collectProjectImages(p);
  const {
    currency,
    fundsRaisedNum,
    hardCapNum,
    sharePriceNum,
    minInvestNum,
    maxInvestNum,
  } = resolveDetailFinancials(p);
  const offeringDocumentDetails = formatOfferingDocumentDetails(p);

  return {
    id,
    title: p.title ?? "—",
    images: allImages.length ? allImages : [],
    description: p.description ?? "",
    status: p.status ?? "—",
    isSample: normalizeIsSample(p.isSample ?? p.is_sample),
    industry: p.industry ?? null,
    industryName: p.industry?.name ?? p.industryName ?? "",
    fundsRaised: formatMoneyAmount(fundsRaisedNum, currency),
    hardCap: formatMoneyAmount(hardCapNum, currency),
    fundsRaisedAmount: Number(fundsRaisedNum),
    hardCapAmount: Number(hardCapNum),
    daysLeft: p.campaignDaysLeft ?? p.daysLeft ?? p.days_left ?? "—",
    investorCount: p.investorCount ?? p.investor_count ?? 0,
    pricePerShare: sharePriceNum ? formatMoneyAmount(sharePriceNum, currency) : "—",
    minimumInvestment: minInvestNum ? formatMoneyAmount(minInvestNum, currency) : "—",
    maximumInvestment: maxInvestNum ? formatMoneyAmount(maxInvestNum, currency) : "—",
    totalFundsRequired: formatMoneyAmount(
      p.totalFundsRequired ?? p.total_funds_required ?? 0,
      currency,
    ),
    projectOverview: p.description ?? p.overview ?? p.projectOverview ?? "",
    companyWebsite: String(p.companyWebsite ?? p.company_website ?? "").trim(),
    companyEmail: String(p.companyEmail ?? p.company_email ?? "").trim(),
    companyPhone: String(p.companyPhone ?? p.company_phone ?? "").trim(),
    companyHeadOfficeAddress: String(p.companyHeadOfficeAddress ?? p.company_head_office_address ?? "").trim(),
    companyOverview: p.companyOverview ?? "",
    teamMembers: mapDetailTeamMembers(p.teamMembers),
    offeringDocumentDetails,
    newsUpdates: mapDetailNewsUpdates(p),
  };
}
